import AdminPermission from "components/admin/AdminPermission";
import {
  LifecycleStatus,
  RewardApplication,
  RewardDefinition,
  RewardType,
  gamificationApi,
} from "api/admin/gamification";
import { FormEvent, useMemo, useState } from "react";
import useAdminAccess from "hooks/useAdminAccess";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import {
  Button,
  Drawer,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  SectionCard,
  StatusPill,
  dateTimeLocal,
  errorMessage,
  fieldClass,
  formatLabel,
} from "./components";

type RewardForm = {
  name: string;
  rewardType: string;
  application: RewardApplication;
  status: LifecycleStatus;
  quantity: string;
  market: string;
  currency: string;
  cashbackValueMode: "fixed" | "percentage";
  fixedAmount: string;
  percentageRate: string;
  floorAmount: string;
  ceilingAmount: string;
  limitBuckets: string[];
  perkCode: string;
  perkNotes: string;
  effectiveFrom: string;
  effectiveTo: string;
  expiresAt: string;
  reason: string;
};

const rewardForm = (reward?: RewardDefinition): RewardForm => ({
  name: reward?.name ?? "",
  rewardType: reward?.rewardType ?? "cashback",
  application: reward?.application ?? "auto",
  status: reward?.status ?? "active",
  quantity: String(reward?.maxUses ?? 1),
  market: reward?.market ?? "",
  currency: reward?.currency ?? "NGN",
  cashbackValueMode:
    reward?.rewardType === "cashback" && reward.percentageRate != null
      ? "percentage"
      : "fixed",
  fixedAmount: String(reward?.fixedAmount ?? ""),
  percentageRate:
    reward?.percentageRate == null
      ? ""
      : reward.rewardType === "fee_discount"
        ? String(Number(reward.percentageRate) * 100)
        : String(reward.percentageRate),
  floorAmount: String(reward?.floorAmount ?? ""),
  ceilingAmount: String(reward?.ceilingAmount ?? ""),
  limitBuckets: reward?.limitBuckets ?? [],
  perkCode: reward?.perkCode ?? "",
  perkNotes: reward?.perkNotes ?? "",
  effectiveFrom: dateTimeLocal(reward?.effectiveFrom),
  effectiveTo: reward?.effectiveTo ? dateTimeLocal(reward.effectiveTo) : "",
  expiresAt: reward?.expiresAt ? dateTimeLocal(reward.expiresAt) : "",
  reason: "",
});

const optionalNumber = (value: string) => (value ? Number(value) : null);

const valueLabel = (reward: RewardDefinition) => {
  if (reward.fixedAmount != null) {
    return `${reward.currency ?? ""} ${Number(reward.fixedAmount).toLocaleString()}`;
  }
  if (reward.percentageRate != null) {
    return reward.rewardType === "cashback"
      ? `${Number(reward.percentageRate) * 100}% cashback`
      : reward.rewardType === "fee_discount"
        ? `${Number(reward.percentageRate) * 100}% off`
      : `${Number(reward.percentageRate)}×`;
  }
  return reward.perkCode ? formatLabel(reward.perkCode) : "Perk";
};

const perkLabel = (rewardType: string) => {
  switch (rewardType) {
    case "access_perk":
      return "Access key";
    case "support_tier":
      return "Support level";
    case "status_social_perk":
      return "Status or social code";
    case "event_invite":
      return "Event code";
    case "manual_physical_item":
      return "Item code";
    default:
      return "Perk code";
  }
};

export default function Rewards() {
  const queryClient = useQueryClient();
  const { can } = useAdminAccess();
  const canReadOptions = can("gamification.overview.view") || can("gamification.rewardDefinitions.view");
  const [editing, setEditing] = useState<RewardDefinition | null | undefined>();
  const [form, setForm] = useState<RewardForm>(rewardForm());
  const rewards = useQuery(
    "gamification-reward-definitions",
    gamificationApi.listRewardDefinitions,
    { enabled: can("gamification.rewardDefinitions.view"), retry: false },
  );
  const rewardTypes = useQuery(
    "gamification-reward-types",
    gamificationApi.listRewardTypes,
    { enabled: canReadOptions, retry: false },
  );
  const options = useQuery(
    "gamification-configuration-options",
    gamificationApi.getConfigurationOptions,
    { enabled: canReadOptions, retry: false },
  );
  const selectedType = useMemo<RewardType | undefined>(
    () =>
      rewardTypes.data?.rewardTypes.find(
        (type) => type.code === form.rewardType,
      ),
    [form.rewardType, rewardTypes.data?.rewardTypes],
  );
  const isFixed = selectedType?.shape === "fixed";
  const isPercentage = selectedType?.shape === "percentage";
  const isPerk = selectedType?.shape === "perk";
  const isFeeDiscount = form.rewardType === "fee_discount";
  const isLimitIncrease = form.rewardType === "transaction_limit_increase";
  const isCashback = form.rewardType === "cashback";
  const isCashbackPercentage = isCashback && form.cashbackValueMode === "percentage";

  const save = useMutation(
    async () => {
      const input = {
        name: form.name.trim(),
        rewardType: form.rewardType,
        application: isPerk ? ("manual" as const) : form.application,
        status: form.status,
        quantity: Number(form.quantity),
        effectiveFrom: new Date(form.effectiveFrom).toISOString(),
        effectiveTo: form.effectiveTo
          ? new Date(form.effectiveTo).toISOString()
          : null,
        market: form.market || null,
        currency: isFixed || isCashback ? form.currency : null,
        fixedAmount: isFixed && !isCashbackPercentage ? optionalNumber(form.fixedAmount) : null,
        percentageRate: isPercentage || isCashbackPercentage
          ? isFeeDiscount
            ? Number(form.percentageRate) / 100
            : isCashbackPercentage
              ? optionalNumber(form.percentageRate) === null ? null : Number(form.percentageRate) / 100
              : optionalNumber(form.percentageRate)
          : null,
        floorAmount: isPercentage || isCashbackPercentage ? optionalNumber(form.floorAmount) : null,
        ceilingAmount: isPercentage || isCashbackPercentage
          ? optionalNumber(form.ceilingAmount)
          : null,
        limitBuckets: isLimitIncrease ? form.limitBuckets : null,
        perkCode: isPerk ? form.perkCode.trim() || null : null,
        perkNotes: isPerk ? form.perkNotes.trim() || null : null,
        expiresAt: form.expiresAt
          ? new Date(form.expiresAt).toISOString()
          : null,
        reason: form.reason.trim() || undefined,
      };
      return editing
        ? gamificationApi.updateRewardDefinition(editing.id, input)
        : gamificationApi.createRewardDefinition(input);
    },
    {
      onSuccess: (result) => {
        toast.success(
          "approvalRequest" in result
            ? "Reward update submitted for approval"
            : editing
              ? "Reward updated"
              : "Reward created",
        );
        queryClient.invalidateQueries("gamification-reward-definitions");
        queryClient.invalidateQueries("gamification-streak-milestones");
        queryClient.invalidateQueries("gamification-tiers");
        setEditing(undefined);
      },
      onError: (error) => {
        toast.error(errorMessage(error));
      },
    },
  );

  const open = (reward: RewardDefinition | null) => {
    setEditing(reward);
    setForm(rewardForm(reward ?? undefined));
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!selectedType || !options.data) return;
    save.mutate();
  };

  const toggleLimitBucket = (bucket: string) => {
    setForm((current) => ({
      ...current,
      limitBuckets: current.limitBuckets.includes(bucket)
        ? current.limitBuckets.filter((value) => value !== bucket)
        : [...current.limitBuckets, bucket],
    }));
  };

  return (
    <>
      <SectionCard
        title="Rewards"
        description="Define a benefit once, scope it to a market, then assign it to streak milestones or ELO tiers."
        action={can("gamification.rewardDefinitions.create") ? (
          <Button onClick={() => open(null)}>
            <i className="fa fa-plus" />
            Add reward
          </Button>
        ) : undefined}
      >
        {rewards.isLoading || rewardTypes.isLoading || options.isLoading ? (
          <LoadingState rows={5} />
        ) : rewards.error || rewardTypes.error || options.error ? (
          <ErrorState
            error={rewards.error || rewardTypes.error || options.error}
            onRetry={() => {
              if (can("gamification.rewardDefinitions.view")) rewards.refetch();
              if (canReadOptions) rewardTypes.refetch();
              if (canReadOptions) options.refetch();
            }}
          />
        ) : !rewards.data?.rewards.length ? (
          <EmptyState
            title="No rewards created"
            body="Create a reward with its value, market and usage quantity, then assign it from a milestone or tier."
            icon="fa-gift"
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100 text-left">
              <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="px-5 py-3">Reward</th>
                  <th className="px-5 py-3">Type</th>
                  <th className="px-5 py-3">Value</th>
                  <th className="px-5 py-3">Quantity</th>
                  <th className="px-5 py-3">Delivery</th>
                  <th className="px-5 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rewards.data.rewards.map((reward) => {
                  const market = options.data?.markets.find(
                    (item) => item.code === reward.market,
                  );
                  return (
                    <tr key={reward.id} className="hover:bg-gray-50/70">
                      <td className="px-5 py-4">
                        <p className="font-semibold text-gray-950">
                          {reward.name}
                        </p>
                        <div className="mt-1 flex items-center gap-2">
                          <StatusPill status={reward.status} />
                          <span className="text-xs text-gray-500">
                            {market?.name ?? reward.market ?? "All markets"}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-700">
                        {formatLabel(reward.rewardType)}
                      </td>
                      <td className="px-5 py-4 text-sm font-semibold text-gray-800">
                        {valueLabel(reward)}
                      </td>
                      <td className="px-5 py-4 text-sm font-semibold text-gray-800">
                        {reward.maxUses.toLocaleString()}
                      </td>
                      <td className="px-5 py-4">
                        <StatusPill status={reward.application} />
                      </td>
                      <td className="px-5 py-4 text-right">
                        {can("gamification.rewardDefinitions.edit") ? <Button variant="quiet" onClick={() => open(reward)}>
                          Edit
                        </Button> : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <AdminPermission
        permission={
          editing
            ? "gamification.rewardDefinitions.edit"
            : "gamification.rewardDefinitions.create"
        }
      >
        <Drawer
          open={editing !== undefined}
          title={editing ? "Edit reward" : "Add reward"}
          description="Configure only the fields used by this reward type."
          onClose={() => setEditing(undefined)}
        >
          <form className="space-y-5" onSubmit={submit}>
            <Field label="Reward name" required>
              <input
                required
                minLength={2}
                maxLength={255}
                className={fieldClass}
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Reward type" required>
                <select
                  className={fieldClass}
                  value={form.rewardType}
                  onChange={(event) => setForm({ ...form, rewardType: event.target.value })}
                >
                  {rewardTypes.data?.rewardTypes.map((type) => (
                    <option key={type.code} value={type.code}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                label="Uses per user"
                required
                hint="How many times one granted reward can be used."
              >
                <input
                  required
                  min={1}
                  max={10_000}
                  type="number"
                  className={fieldClass}
                  value={form.quantity}
                  onChange={(event) => setForm({ ...form, quantity: event.target.value })}
                />
              </Field>
            </div>

            <Field
              label="Market scope"
              required
              hint="All markets is global. Choose one country to restrict eligibility."
            >
              <select
                className={fieldClass}
                value={form.market}
                onChange={(event) => setForm({ ...form, market: event.target.value })}
              >
                {options.data?.markets.map((market) => (
                  <option key={market.code ?? "global"} value={market.code ?? ""}>
                    {market.name}
                  </option>
                ))}
              </select>
            </Field>

            {isCashback && (
              <Field label="Cashback value" hint="Choose one value model for this reward.">
                <select
                  className={fieldClass}
                  value={form.cashbackValueMode}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      cashbackValueMode: event.target.value as "fixed" | "percentage",
                    })
                  }
                >
                  <option value="fixed">Fixed amount</option>
                  <option value="percentage">Percentage of eligible spend</option>
                </select>
              </Field>
            )}

            {(isFixed || isCashback) && !isCashbackPercentage && (
              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="Currency"
                  required
                  hint="Users must have this wallet currency to receive the reward."
                >
                  <select
                    required
                    className={fieldClass}
                    value={form.currency}
                    onChange={(event) => setForm({ ...form, currency: event.target.value })}
                  >
                    {options.data?.currencies.map((currency) => (
                      <option key={currency.code} value={currency.code}>
                        {currency.code} · {currency.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field
                  label={isLimitIncrease ? "Limit increase amount" : "Cashback amount"}
                  required
                >
                  <input
                    required
                    min={0.01}
                    max={5_000_000}
                    step="0.01"
                    type="number"
                    className={fieldClass}
                    value={form.fixedAmount}
                    onChange={(event) =>
                      setForm({ ...form, fixedAmount: event.target.value })
                    }
                  />
                </Field>
              </div>
            )}

            {isLimitIncrease && (
              <fieldset>
                <legend className="text-sm font-medium text-gray-700">
                  Limits to increase <span className="text-orange-600">*</span>
                </legend>
                <p className="mt-1 text-xs text-gray-500">
                  Select at least one limit bucket.
                </p>
                <div className="mt-3 grid gap-3 sm:grid-cols-3">
                  {options.data?.limitBuckets.map((bucket) => (
                    <label
                      key={bucket.code}
                      className="flex cursor-pointer items-center gap-3 rounded-lg border border-gray-200 px-3 py-3 text-sm text-gray-700 hover:border-orange-300"
                    >
                      <input
                        checked={form.limitBuckets.includes(bucket.code)}
                        required={form.limitBuckets.length === 0}
                        type="checkbox"
                        value={bucket.code}
                        onChange={() => toggleLimitBucket(bucket.code)}
                      />
                      {bucket.name}
                    </label>
                  ))}
                </div>
              </fieldset>
            )}

            {(isPercentage || isCashbackPercentage) && (
              <div className="rounded-xl border border-gray-200 p-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field
                    label={
                      isFeeDiscount || isCashbackPercentage ? "Percentage" : "Multiplier"
                    }
                    required
                    hint={
                      isFeeDiscount || isCashbackPercentage
                        ? "Enter 10 for 10%."
                        : "Enter 2 for 2× cashback."
                    }
                  >
                    <input
                      required
                      min={0.01}
                      max={isFeeDiscount || isCashbackPercentage ? 100 : 5}
                      step="0.01"
                      type="number"
                      className={fieldClass}
                      value={form.percentageRate}
                      onChange={(event) =>
                        setForm({ ...form, percentageRate: event.target.value })
                      }
                    />
                  </Field>
                  <div className="hidden sm:block" />
                  <Field
                    label="Minimum benefit"
                    hint="Optional minimum value applied per use."
                  >
                    <input
                      min={0}
                      step="0.01"
                      type="number"
                      className={fieldClass}
                      value={form.floorAmount}
                      onChange={(event) =>
                        setForm({ ...form, floorAmount: event.target.value })
                      }
                    />
                  </Field>
                  <Field
                    label="Maximum benefit"
                    hint="Optional maximum value applied per use."
                  >
                    <input
                      min={0}
                      step="0.01"
                      type="number"
                      className={fieldClass}
                      value={form.ceilingAmount}
                      onChange={(event) =>
                        setForm({ ...form, ceilingAmount: event.target.value })
                      }
                    />
                  </Field>
                </div>
              </div>
            )}

            {isPerk && (
              <div className="rounded-xl border border-gray-200 p-4">
                <Field
                  label={perkLabel(form.rewardType)}
                  required
                  hint="A stable internal identifier used by fulfilment and audit records."
                >
                  <input
                    required
                    maxLength={128}
                    className={fieldClass}
                    placeholder="e.g. priority_support"
                    value={form.perkCode}
                    onChange={(event) => setForm({ ...form, perkCode: event.target.value })}
                  />
                </Field>
                <Field
                  className="mt-4"
                  label="Fulfilment instructions"
                  hint="Describe exactly what Operations needs to provide."
                >
                  <textarea
                    rows={3}
                    className={fieldClass}
                    value={form.perkNotes}
                    onChange={(event) => setForm({ ...form, perkNotes: event.target.value })}
                  />
                </Field>
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Delivery"
                hint={isPerk ? "This reward type requires manual fulfilment." : undefined}
              >
                <select
                  disabled={isPerk}
                  className={fieldClass}
                  value={isPerk ? "manual" : form.application}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      application: event.target.value as RewardApplication,
                    })
                  }
                >
                  <option value="auto">Automatically</option>
                  <option value="manual">Admin completes it</option>
                </select>
              </Field>
              <Field label="Status">
                <select
                  className={fieldClass}
                  value={form.status}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      status: event.target.value as LifecycleStatus,
                    })
                  }
                >
                  {(["draft", "scheduled", "active", "archived"] as LifecycleStatus[]).map(
                    (status) => (
                      <option key={status} value={status}>
                        {formatLabel(status)}
                      </option>
                    ),
                  )}
                </select>
              </Field>
            </div>

            <details className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <summary className="cursor-pointer text-sm font-semibold text-gray-700">
                Availability
              </summary>
              <div className="mt-5 space-y-5">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Starts" required>
                    <input
                      required
                      type="datetime-local"
                      className={fieldClass}
                      value={form.effectiveFrom}
                      onChange={(event) =>
                        setForm({ ...form, effectiveFrom: event.target.value })
                      }
                    />
                  </Field>
                  <Field label="Ends">
                    <input
                      min={form.effectiveFrom}
                      type="datetime-local"
                      className={fieldClass}
                      value={form.effectiveTo}
                      onChange={(event) =>
                        setForm({ ...form, effectiveTo: event.target.value })
                      }
                    />
                  </Field>
                </div>
                <Field
                  label="User entitlement expires"
                  hint="Optional expiry for each user's granted reward."
                >
                  <input
                    type="datetime-local"
                    className={fieldClass}
                    value={form.expiresAt}
                    onChange={(event) => setForm({ ...form, expiresAt: event.target.value })}
                  />
                </Field>
              </div>
            </details>

            <Field label="Reason for change">
              <textarea
                rows={2}
                className={fieldClass}
                value={form.reason}
                onChange={(event) => setForm({ ...form, reason: event.target.value })}
              />
            </Field>

            <div className="flex justify-end gap-3 border-t border-gray-100 pt-5">
              <Button type="button" variant="secondary" onClick={() => setEditing(undefined)}>
                Cancel
              </Button>
              <Button disabled={save.isLoading || !selectedType || !options.data} type="submit">
                {save.isLoading ? "Saving…" : editing ? "Save changes" : "Add reward"}
              </Button>
            </div>
          </form>
        </Drawer>
      </AdminPermission>
    </>
  );
}
